import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './button.js';

interface Props {
  children: ReactNode;
  /** Shown instead of the generic copy when a screen can say something better. */
  title?: string;
}

interface State {
  error: Error | null;
}

/**
 * The last line of defence (PLAN §13.1 asks for one).
 *
 * A render crash must not leave a blank screen on a phone — the owner needs a
 * way back and, because this is self-hosted and there is no support desk, the
 * actual message to send to the developer.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // There is no error service; the console is where the owner can find it.
    console.error('Screen crashed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="mx-auto max-w-md p-6">
        <h1 className="text-lg font-semibold text-ink">
          {this.props.title ?? 'This screen stopped working'}
        </h1>
        <p className="mt-2 text-md leading-relaxed text-muted">
          Nothing was lost — drafts are saved on the server. Reload to carry on.
        </p>
        <pre className="mt-4 overflow-x-auto rounded-aw border border-line bg-raise p-3 font-mono text-xs text-muted">
          {error.message}
        </pre>
        <div className="mt-4 flex gap-2">
          <Button
            variant="primary"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload
          </Button>
          <Button
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }
}
